import React, { useEffect, useState } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Download, Filter, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '../utils/excelExport';

const REPORT_CONFIG = {
  cake: {
    title: 'Cake Detailed Report',
    itemLabel: 'Cake Name',
    exportFilename: 'Cake_Detailed_Report',
    sheetName: 'Cake Report'
  },
  decoration: {
    title: 'Extra Decorations Detailed Report',
    itemLabel: 'Decoration Item',
    exportFilename: 'Decorations_Detailed_Report',
    sheetName: 'Decoration Report'
  },
  food: {
    title: 'Food & Beverages Detailed Report',
    itemLabel: 'F&B Item',
    exportFilename: 'Food_Beverages_Detailed_Report',
    sheetName: 'F&B Report'
  },
  miscellaneous: {
    title: 'Miscellaneous Items Detailed Report',
    itemLabel: 'Misc Item',
    exportFilename: 'Miscellaneous_Detailed_Report',
    sheetName: 'Misc Report'
  }
};

export default function ItemDetailedReport({ itemType }) {
  const { user } = useAuth();
  const config = REPORT_CONFIG[itemType];

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_quantity: 0, total_amount: 0, total_rows: 0 });
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchBranches();
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstOfMonth.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, [user?.id]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch {
      console.error('Failed to fetch branches');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `/reports/item-detailed/${itemType}?`;
      if (branchId && branchId !== 'all') url += `branch_id=${branchId}&`;
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;

      const response = await api.get(url);
      setItems(response.data.items);
      setSummary(response.data.summary);
      setFetched(true);
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = items.map(item => ({
      'Date': item.date,
      'Branch': item.branch_name,
      'Theater': item.theater_name,
      'Time Slot': item.time_slot,
      [config.itemLabel]: item.item_name,
      'Qty': item.quantity,
      'Amount': item.amount
    }));

    exportData.push({
      'Date': '',
      'Branch': '',
      'Theater': '',
      'Time Slot': '',
      [config.itemLabel]: 'TOTAL',
      'Qty': summary.total_quantity,
      'Amount': summary.total_amount
    });

    const branchName = branchId && branchId !== 'all'
      ? branches.find(b => b.id === branchId)?.name || 'All'
      : 'All_Branches';
    const dateStr = startDate && endDate ? `${startDate}_to_${endDate}` : '';
    const filename = `${config.exportFilename}_${branchName}_${dateStr}`;

    exportToExcel(exportData, filename, config.sheetName);
    toast.success('Report exported successfully');
  };

  return (
    <div className="space-y-6" data-testid={`${itemType}-detailed-report`}>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="report-title">{config.title}</h1>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Branch</label>
              <Select value={branchId} onValueChange={setBranchId} data-testid="branch-filter">
                <SelectTrigger data-testid="branch-filter-trigger">
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}, {b.location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-filter"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-filter"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={fetchReport} disabled={loading} className="flex-1" data-testid="apply-filters-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Filter className="h-4 w-4 mr-2" />}
                Apply Filters
              </Button>
              {fetched && items.length > 0 && (
                <Button variant="outline" onClick={handleExport} data-testid="export-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Excel
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {fetched && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">Total Items Sold</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="total-rows">{summary.total_rows}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">Total Quantity</p>
              <p className="text-2xl font-bold text-indigo-600" data-testid="total-qty">{summary.total_quantity}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">Total Amount</p>
              <p className="text-2xl font-bold text-emerald-600" data-testid="total-amount">₹{summary.total_amount.toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Data Table */}
      {fetched && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {items.length > 0 ? `${items.length} records found` : 'No records found'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {items.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">#</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Branch</TableHead>
                      <TableHead className="font-semibold">Theater</TableHead>
                      <TableHead className="font-semibold">Time Slot</TableHead>
                      <TableHead className="font-semibold">{config.itemLabel}</TableHead>
                      <TableHead className="font-semibold text-right">Qty</TableHead>
                      <TableHead className="font-semibold text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={`${item.bill_id}-${idx}`} data-testid={`item-row-${idx}`}>
                        <TableCell className="text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="whitespace-nowrap">{item.date}</TableCell>
                        <TableCell>{item.branch_name}</TableCell>
                        <TableCell>{item.theater_name}</TableCell>
                        <TableCell className="whitespace-nowrap">{item.time_slot}</TableCell>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right font-medium">₹{item.amount.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Summary Row */}
                    <TableRow className="bg-slate-100 font-bold border-t-2">
                      <TableCell colSpan={6} className="text-right text-slate-700">TOTAL</TableCell>
                      <TableCell className="text-right text-indigo-700" data-testid="table-total-qty">{summary.total_quantity}</TableCell>
                      <TableCell className="text-right text-emerald-700" data-testid="table-total-amount">₹{summary.total_amount.toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <p className="text-lg">No records found for the selected filters.</p>
                <p className="text-sm mt-1">Try adjusting your date range or branch selection.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial State */}
      {!fetched && !loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-slate-500">
              <Filter className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-lg">Select filters and click Apply to generate the report</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
